import React, { useCallback, useEffect, useState } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import Icon from 'components/AppIcon';
import { getRevenueDashboard } from 'services/reportingService';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import DatePicker from 'components/ui/Datepicker';
import { getCurrentDateString } from 'utils/DateUtil';

const currencyFormatter = new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat('en-ZA');
const revenueColors = ['#147d64', '#e5a33d'];
const bookingColors = ['#147d64', '#d99a2b', '#3182a6', '#b94a48'];

const formatCurrency = (value) => currencyFormatter.format(Number(value) || 0);

const Metric = ({ label, value, detail, icon, tone }) => (
    <section className="min-w-0 rounded-lg border border-border bg-card p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <p className="text-sm font-medium text-muted-foreground">{label}</p>
                <p className="mt-2 break-words text-2xl font-semibold text-foreground">{value}</p>
                <p className="mt-1 text-sm text-muted-foreground">{detail}</p>
            </div>
            <span className={`rounded-md p-2 ${tone}`}>
                <Icon name={icon} size={20} />
            </span>
        </div>
    </section>
);

const RevenueReport = ({ businessId }) => {
    const [dashboard, setDashboard] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    //Get Current Date
    const currentDate = getCurrentDateString();
    const [reportFilter, setReportFilter] = useState({ fromDate: currentDate, toDate: currentDate, revenueType: 0 });

    const loadDashboard = useCallback(async () => {
        setIsLoading(true);
        setError('');

        if(businessId == null || businessId === "" || businessId === undefined) {
            setError('Business ID is not available.');
            setIsLoading(false);
            return;
        }

        try {
            const response = await getRevenueDashboard(businessId, reportFilter.fromDate, reportFilter.toDate, reportFilter.revenueType);
            if (!response?.success || !response?.data) {
                throw new Error(response?.message || 'Revenue report data is unavailable.');
            }
            setDashboard(response.data);
        } catch (requestError) {
            setError(requestError?.message || 'Unable to load the revenue report.');
        } finally {
            setIsLoading(false);
        }
    }, [businessId, reportFilter]);

    useEffect(() => {
        //const currentDate = getCurrentDateString();
        //setReportFilter({ fromDate: currentDate, toDate: currentDate, revenueType: 0 });
        loadDashboard();
    }, [businessId]);

    if (isLoading) {
        return (
            <div className="mt-6 rounded-lg border border-border bg-card p-8 text-center text-muted-foreground" role="status">
                Loading revenue report...
            </div>
        );
    }

    if (error) {
        return (
            <div className="mt-6 rounded-lg border border-destructive/30 bg-card p-6" role="alert">
                <p className="font-medium text-destructive">{error}</p>
                <button
                    type="button"
                    onClick={() => loadDashboard(reportFilter)}
                    className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                >
                    <Icon name="RefreshCw" size={16} />
                    Try again
                </button>
            </div>
        );
    }

    const revenueByPeriod = dashboard.revenueByPeriod || [];
    const topServices = dashboard.topServices || [];
    const bookingStatus = [
        { name: 'Completed', value: Number(dashboard.completedBookings) || 0 },
        { name: 'Pending', value: Number(dashboard.pendingBookings) || 0 },
        { name: 'In progress', value: Number(dashboard.inProgressBookings) || 0 },
        { name: 'Cancelled', value: Number(dashboard.cancelledBookings) || 0 },
    ];
    const hasBookings = bookingStatus.some((status) => status.value > 0);
    const comparison = dashboard.comparison;
    const comparisonDetail = comparison?.previousPeriodRevenue > 0
        ? `${comparison.isIncrease ? '+' : '-'}${Number(comparison.changePercentage).toFixed(1)}% vs previous period`
        : 'No previous-period baseline';

    return (
        <div className="mt-6 space-y-6">
            <div className="flex flex-col gap-4 border border-border bg-white p-4 lg:flex-row lg:items-end lg:justify-between">
                {/* <div>
                    <h2 className="text-xl font-semibold text-foreground">Revenue overview</h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {dashboard.description || 'Revenue and booking performance for the selected period.'}
                    </p>
                </div> */}
                <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:max-w-xl">
                    <DatePicker
                        label="From Date"
                        value={reportFilter.fromDate}
                        max={reportFilter.toDate || undefined}
                        onChange={(val) => setReportFilter((current) => ({ ...current, fromDate: val }))}
                    />
                    <DatePicker
                        label="To Date"
                        value={reportFilter.toDate}
                        minDate={reportFilter.fromDate || undefined}
                        onChange={(val) => setReportFilter((current) => ({ ...current, toDate:val }))}
                    />
                </div>
                <Button
                    type="button"
                    onClick={() => loadDashboard(reportFilter)}
                    iconName="RefreshCw"
                    iconSize={16}
                    disabled={Boolean(reportFilter.fromDate && reportFilter.toDate && reportFilter.fromDate > reportFilter.toDate)}
                >
                    Refresh
                </Button>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <Metric
                    label="Total revenue"
                    value={formatCurrency(dashboard.totalRevenue)}
                    detail={`${numberFormatter.format(dashboard.totalBookings || 0)} bookings`}
                    icon="Wallet"
                    tone="bg-emerald-50 text-emerald-700"
                />
                <Metric
                    label="Settled revenue"
                    value={formatCurrency(dashboard.settledRevenue)}
                    detail={`${Number(dashboard.settlementRate || 0).toFixed(1)}% of total revenue`}
                    icon="CircleCheck"
                    tone="bg-sky-50 text-sky-700"
                />
                <Metric
                    label="Outstanding revenue"
                    value={formatCurrency(dashboard.outstandingRevenue)}
                    detail="Awaiting settlement"
                    icon="Clock3"
                    tone="bg-amber-50 text-amber-700"
                />
                <Metric
                    label="Average booking value"
                    value={formatCurrency(dashboard.averageBookingValue)}
                    detail={comparisonDetail}
                    icon="ChartNoAxesCombined"
                    tone="bg-rose-50 text-rose-700"
                />
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
                <section className="min-w-0 rounded-lg border border-border bg-card p-5 xl:col-span-3">
                    <div className="mb-5">
                        <h3 className="font-semibold text-foreground">Revenue by period</h3>
                        <p className="mt-1 text-sm text-muted-foreground">Settled and outstanding amounts</p>
                    </div>
                    {revenueByPeriod.length ? (
                        <div className="h-72 w-full" role="img" aria-label="Bar chart showing revenue by period, split into settled and outstanding revenue">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={revenueByPeriod} margin={{ top: 8, right: 12, left: 8, bottom: 8 }}>
                                    <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="period" tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} />
                                    <YAxis tickFormatter={(value) => `R${numberFormatter.format(value)}`} tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} width={64} />
                                    <Tooltip formatter={(value, name) => [formatCurrency(value), name]} />
                                    <Legend />
                                    <Bar dataKey="settledRevenue" name="Settled" stackId="revenue" fill={revenueColors[0]} radius={[0, 0, 0, 0]} />
                                    <Bar dataKey="outstandingRevenue" name="Outstanding" stackId="revenue" fill={revenueColors[1]} radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <p className="flex h-72 items-center justify-center text-sm text-muted-foreground">No period revenue is available.</p>
                    )}
                </section>

                <section className="min-w-0 rounded-lg border border-border bg-card p-5 xl:col-span-2">
                    <div className="mb-5">
                        <h3 className="font-semibold text-foreground">Booking status</h3>
                        <p className="mt-1 text-sm text-muted-foreground">{numberFormatter.format(dashboard.totalBookings || 0)} total bookings</p>
                    </div>
                    {hasBookings ? (
                        <div className="flex flex-col items-center gap-4 sm:flex-row xl:flex-col 2xl:flex-row">
                            <div className="h-48 w-full max-w-56" role="img" aria-label="Donut chart showing bookings by status">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={bookingStatus.filter((status) => status.value > 0)} dataKey="value" nameKey="name" innerRadius={52} outerRadius={78} paddingAngle={3}>
                                            {bookingStatus.filter((status) => status.value > 0).map((status) => (
                                                <Cell key={status.name} fill={bookingColors[bookingStatus.indexOf(status)]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value) => [numberFormatter.format(value), 'Bookings']} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <dl className="w-full space-y-3">
                                {bookingStatus.map((status, index) => (
                                    <div key={status.name} className="flex items-center justify-between gap-3 text-sm">
                                        <dt className="flex items-center gap-2 text-muted-foreground">
                                            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: bookingColors[index] }} />
                                            {status.name}
                                        </dt>
                                        <dd className="font-semibold text-foreground">{numberFormatter.format(status.value)}</dd>
                                    </div>
                                ))}
                                <div className="border-t border-border pt-3 text-sm text-muted-foreground">
                                    Completion rate <span className="float-right font-semibold text-foreground">{Number(dashboard.completionRate || 0).toFixed(1)}%</span>
                                </div>
                            </dl>
                        </div>
                    ) : (
                        <p className="flex h-48 items-center justify-center text-sm text-muted-foreground">No bookings are available for this period.</p>
                    )}
                </section>
            </div>

            <section className="rounded-lg border border-border bg-card p-5">
                <div className="mb-5">
                    <h3 className="font-semibold text-foreground">Top services</h3>
                    <p className="mt-1 text-sm text-muted-foreground">Revenue and booking count by service</p>
                </div>
                {topServices.length ? (
                    <div className="h-72 w-full" role="img" aria-label="Horizontal bar chart showing revenue by top service">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={topServices} layout="vertical" margin={{ top: 4, right: 20, left: 12, bottom: 4 }}>
                                <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" tickFormatter={(value) => `R${numberFormatter.format(value)}`} tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} />
                                <YAxis type="category" dataKey="serviceName" width={110} tick={{ fill: '#334155', fontSize: 12 }} axisLine={false} tickLine={false} />
                                <Tooltip formatter={(value) => [formatCurrency(value), 'Revenue']} />
                                <Bar dataKey="totalRevenue" name="Revenue" fill="#3182a6" radius={[0, 4, 4, 0]} barSize={28} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                ) : (
                    <p className="py-12 text-center text-sm text-muted-foreground">No service revenue is available.</p>
                )}
            </section>
        </div>
    );
}

export default RevenueReport;